<template>
  <BaseCard>
    <BaseCardHeader>
      <div class="flex justify-between items-baseline -mr-2">
        <h5 class="font-light">Tags</h5>
        <BaseIconButton v-if="!edit" icon="edit" brand @click="edit = !edit" />
        <template v-else-if="!updating">
          <div class="space-x-2 justify-end">
            <BaseIconButton icon="times" silver @click="doRevert" />
            <BaseIconButton green icon="check" @click="doSubmit" />
          </div>
        </template>
      </div>
    </BaseCardHeader>
    <BaseCardBody>
      <ul class="-m-4">
        <template v-for="(options, tagCategoryId, idx) in tagByCategory">
          <BaseList nested :key="idx" :class="{ 'border-t': idx > 0 }">
            <BaseListHeader>
              <span class="uppercase text-sm">{{ getTagCategoryNameById(tagCategoryId) }}</span>
            </BaseListHeader>
            <BaseListBody>
              <BaseListItem>
                <div class="flex -mx-4 items-center">
                  <div class="flex-1 px-4">
                    <ul class="-m-1 flex flex-wrap">
                      <li v-for="option in options" :key="option.id" class="p-1">
                        <BaseBadge
                          :custom="localTags.indexOf(option.id) > -1"
                          :silver="localTags.indexOf(option.id) === -1"
                          :outline="localTags.indexOf(option.id) === -1"
                          :style="getStyles(option)"
                          @click="toggleTag(option)"
                          class="select-none"
                          :class="{ 'cursor-pointer': edit }"
                        >
                          {{ option.name }}
                        </BaseBadge>
                      </li>
                    </ul>
                  </div>
                  <div v-if="updating" class="flex-none px-4">
                    <Loader small />
                  </div>
                </div>
              </BaseListItem>
            </BaseListBody>
          </BaseList>
        </template>
      </ul>
    </BaseCardBody>
  </BaseCard>
</template>

<script>
import { groupBy, filter } from 'lodash-es';
import { entityComputed } from '@/store/helper';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

export default {
  name: 'Tags',
  mixins: [veeValidateErrorsMixin],
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      edit: false,
      updating: false,
      localTags: [],
    };
  },
  computed: {
    ...entityComputed,
    tagByCategory() {
      return groupBy(
        filter(
          this.$store.state.entities.tags.byId,
          (tag) => tag.taggableBy.indexOf('accounts') >= 0
        ),
        'tagCategoryId'
      );
    },
    tagIds() {
      return this.$currentAccount.tags || [];
    },
  },
  watch: {
    tagIds: {
      handler(ids) {
        this.localTags = JSON.parse(JSON.stringify(ids));
      },
      deep: true,
    },
  },
  created() {
    this.localTags = JSON.parse(JSON.stringify(this.tagIds));
  },
  methods: {
    getTagCategoryNameById(tagCategoryId) {
      const tagCategory = this.$store.state.entities.tagCategories.byId[tagCategoryId];

      if (!tagCategory) {
        return 'uncategorized';
      }

      return tagCategory.name;
    },
    getStyles(option) {
      const index = this.localTags.indexOf(option.id);
      const exists = index > -1;
      const hasCustomProperties =
        typeof option.customProperties === 'object' && option.customProperties !== null;
      if (exists && hasCustomProperties) {
        return option.customProperties;
      }

      return {};
    },
    toggleTag(tag) {
      if (!this.edit) return;
      const index = this.localTags.indexOf(tag.id);
      const exists = index > -1;
      if (exists) {
        this.localTags.splice(index, 1);
      } else {
        this.localTags.push(tag.id);
      }
    },
    doRevert() {
      this.localTags = JSON.parse(JSON.stringify(this.tagIds));
      this.edit = false;
    },
    async doSubmit() {
      const { dispatch } = this.$store;
      const { accountId } = this;
      const accountUpdatedData = { tags: [...this.localTags] };

      this.updating = true;

      const dispatchWithErrorHandling = this.handleFormErrors(dispatch);

      await dispatchWithErrorHandling('ui/accounts/updateAccount', {
        accountId,
        accountUpdatedData,
      });

      this.updating = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.edit = false;
          })
          .catch((error) => {
            const { status = null, data } = error;
            if (status === HTTP_UNPROCESSABLE_ENTITY) {
              this.doInsertVeeValidateErrors(data.errors, 'accountTags');
            } else {
              this.errorMessage = 'Something went wrong! Please try again';
            }
          });
    },
  },
};
</script>
